import { useState } from 'react';
import type { DocumentPageSchema } from '@avisoma/shared';
import { useNavigate, useParams } from 'react-router-dom';
import backend from '../services/backend';
import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ArrowUpOnSquareIcon,
  ChevronDownIcon,
  DocumentIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import PageDocuments from '../components/PageDocuments';
import PageReconstructedDocuments from '../components/PageReconstructedDocuments';
import AuthGuard from '../components/AuthGuard';
import PageSimilarPages from '../components/PageSimilarPages';
import { PageHeader } from '../components/PageHeader';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '../catalyst/button';
import {
  Dropdown,
  DropdownButton,
  DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from '../catalyst/dropdown';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../components/Toast';
import { readApiErrorMessage } from '../lib/apiErrorMessage';
import ResourceAuditLog from '../components/ResourceAuditLog';
import { DebugObject } from '../components/DebugObject';
import { entityViewTitle } from '../components/ID';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { mimeTypeToExtension } from '../lib/mimeTypeToExtension';
import { PageInfoCard } from './documentPageView/PageInfoCard';
import { ProcessingStatusCard } from './documentPageView/ProcessingStatusCard';
import { DataExtractionResults } from './documentPageView/DataExtractionResults';
import { DeleteConfirmDialog } from './documentPageView/DeleteConfirmDialog';
import { PagePreviewCard } from './documentPageView/PagePreviewCard';
import { usePageTextLocations } from './documentPageView/usePageTextLocations';
import { CurationCard } from './documentPageView/CurationCard';
import { ParentPageCard } from './documentPageView/ParentPageCard';

export function DocumentPageContent({
  id,
  embedded = false,
  onClose,
}: {
  id: string;
  embedded?: boolean;
  onClose?: () => void;
}) {
  useDocumentTitle(embedded ? undefined : entityViewTitle(id, 'Document Page'));
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const isAdmin = auth.hasAccess(['admin']);
  const toast = useToast();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // Section band highlighted on the preview while hovering a segment thumbnail.
  const [highlightBounds, setHighlightBounds] = useState<DocumentPageSchema['sectionBounds']>(null);
  // DR-2004: preview/fullscreen/download variant toggle, lifted here so the
  // Actions-dropdown download follows the same choice as the preview card.
  const [previewVariant, setPreviewVariant] = useState<'rotated' | 'original'>('rotated');
  // The route re-renders (not remounts) on :id change; reset the variant to the stored
  // page on navigation (React's "adjust state during render" pattern — avoids an effect
  // and its set-state-in-effect warning) instead of carrying "original" onto the next page.
  const [variantForPageId, setVariantForPageId] = useState(id);
  if (id !== variantForPageId) {
    setVariantForPageId(id);
    setPreviewVariant('rotated');
  }

  const deleteDocumentPageMutation = useMutation({
    mutationFn: (pageId: string) => backend.deleteDocumentPage(pageId),
    onSuccess: ({ data }) => {
      const pending = data.purge?.pending ?? 0;
      if (pending > 0) {
        toast.info(
          `Page deleted. ${pending} storage object${pending === 1 ? ' is' : 's are'} still being cleaned up and will be retried automatically.`,
        );
      }
      if (embedded) {
        onClose?.();
      } else {
        navigate('/document-pages');
      }
    },
    onError: (error) => {
      // A page still linked to another document answers 409 with the reason (DR-6226).
      setShowDeleteConfirm(false);
      toast.error(readApiErrorMessage(error) ?? 'Failed to delete page');
    },
  });

  const reuploadMutation = useMutation({
    mutationFn: (path: string) => backend.uploadDocuments({ url: path }),
    onSuccess: (response) => {
      const documentId = response.data.documentIds.shift();
      if (embedded) {
        onClose?.();
      } else if (documentId) {
        navigate(`/documents/${documentId}`);
      }
    },
  });

  const documentPage = useQuery({
    queryKey: ['documentPage', id],
    queryFn: () => {
      if (!id) {
        throw new Error('No document page ID provided');
      }
      return backend.getDocumentPage(id).then((res) => res.data);
    },
    enabled: !!id,
    refetchInterval: (data) => {
      const page = data.state.data;
      if (!page) return false;
      // A multi-section parent is terminal at `segmented` — it's replaced by its
      // child segment-pages and never runs the downstream stages below, so their
      // statuses stay undefined. Stop polling here instead of forever.
      if (page.processing?.segmentation?.status === 'segmented') return false;
      const statuses = [
        page?.processing?.pdfParser?.status,
        page?.processing?.metadata?.status,
        page?.processing?.embeddings?.status,
        page?.processing?.classification?.status,
        page?.processing?.dataExtraction?.status,
      ];
      if (statuses.every((status) => status && ['done', 'error', 'skip'].includes(status))) {
        return false;
      }
      return 2000;
    },
  });

  const textLocations = usePageTextLocations(
    id,
    previewVariant,
    documentPage.data?.processing?.dataExtraction?.result,
  );

  if (documentPage.isLoading) {
    return (
      <div className="">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 dark:bg-zinc-700 rounded w-1/4 mb-4"></div>
          <div className="h-64 bg-gray-200 dark:bg-zinc-700 rounded mb-4"></div>
          <div className="h-32 bg-gray-200 dark:bg-zinc-700 rounded"></div>
        </div>
      </div>
    );
  }

  if (documentPage.isError || !documentPage.data) {
    return (
      <div className="">
        <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
                {documentPage.error?.message || 'Document page not found'}
              </h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const page = documentPage.data;
  const pdfUrl = `/api/document-pages/${page.id}/download?inline=1`;

  const downloadFileName = (() => {
    const ext = mimeTypeToExtension(page.mimeType);
    const meta = page.processing?.metadata?.status === 'done' ? page.processing.metadata.result : undefined;
    const suggested = meta?.suggestedFilename?.trim();
    const stem = suggested && suggested.length > 0 ? suggested : page.id;
    return stem.toLowerCase().endsWith(ext.toLowerCase()) ? stem : `${stem}${ext}`;
  })();

  return (
    <div className="">
      {/* Header */}
      <PageHeader
        showBackButton={!embedded}
        title={entityViewTitle(id, 'Document Page')}
        description="View and manage your document page"
        icon={<DocumentIcon className="size-6" />}
      >
        <div className="flex shrink-0 items-center gap-2">
          <DebugObject object={page} />

          <Dropdown>
            <DropdownButton
              as={Button}
              outline
              disabled={reuploadMutation.isPending || deleteDocumentPageMutation.isPending}
            >
              {reuploadMutation.isPending ? (
                <>
                  <ArrowPathIcon className="animate-spin" />
                  Reuploading…
                </>
              ) : deleteDocumentPageMutation.isPending ? (
                <>
                  <ArrowPathIcon className="animate-spin" />
                  Deleting…
                </>
              ) : (
                <>Actions</>
              )}
              <ChevronDownIcon />
            </DropdownButton>
            <DropdownMenu anchor="bottom end">
              <DropdownItem
                href={`/api/document-pages/${page.id}/download${previewVariant === 'original' ? '?variant=original' : ''}`}
                download={
                  previewVariant === 'original' && page.preRotationHash
                    ? downloadFileName.replace(/(\.[^.]+)?$/, (ext) => `-original${ext ?? ''}`)
                    : downloadFileName
                }
              >
                <ArrowDownTrayIcon />
                <DropdownLabel>Download</DropdownLabel>
              </DropdownItem>
              {isAdmin && (
                <>
                  <DropdownDivider />
                  <DropdownItem onClick={() => reuploadMutation.mutate(page.path)}>
                    <ArrowUpOnSquareIcon />
                    <DropdownLabel>Reupload as Document</DropdownLabel>
                  </DropdownItem>
                  <DropdownDivider />
                  <DropdownItem onClick={() => setShowDeleteConfirm(true)}>
                    <TrashIcon className="text-red-600 dark:text-red-400" />
                    <DropdownLabel className="text-red-600 dark:text-red-400">Delete Page</DropdownLabel>
                  </DropdownItem>
                </>
              )}
            </DropdownMenu>
          </Dropdown>
        </div>
      </PageHeader>

      <DeleteConfirmDialog
        open={showDeleteConfirm}
        isPending={deleteDocumentPageMutation.isPending}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => deleteDocumentPageMutation.mutate(page.id)}
      />

      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Left Column - Page Details */}
        <div className="min-w-0 space-y-6">
          {page.parentPageId && (
            <ParentPageCard parentPageId={page.parentPageId} sectionBounds={page.sectionBounds} />
          )}
          <PageInfoCard
            page={page}
            onCategoryUpdated={() => {
              void documentPage.refetch();
              // Consensus is computed against the page's current category, so a
              // category change makes the Curation card's counts stale.
              void queryClient.invalidateQueries({ queryKey: ['pageCurationReviews', id] });
            }}
          />
          <AuthGuard roles={['admin', 'supervisor']}>
            <CurationCard pageId={page.id} />
          </AuthGuard>
          <ProcessingStatusCard page={page} onHoverSection={setHighlightBounds} />
          <DataExtractionResults page={page} onHoverText={textLocations.hoverText} />
        </div>

        {/* Right Column - Page Viewer */}
        <div className="min-w-0 space-y-6">
          <PagePreviewCard
            page={page}
            pdfUrl={pdfUrl}
            highlightBounds={highlightBounds}
            variant={previewVariant}
            onVariantChange={setPreviewVariant}
            fieldHighlight={textLocations.highlight}
          />
        </div>
      </div>

      <AuthGuard roles={['admin']}>{id && <ResourceAuditLog pageId={id} />}</AuthGuard>

      <AuthGuard roles={['admin', 'supervisor', 'curator']}>
        {id && <PageDocuments pageId={id} />}
      </AuthGuard>

      <AuthGuard roles={['admin', 'supervisor', 'curator']}>
        {id && <PageReconstructedDocuments pageId={id} />}
      </AuthGuard>

      <AuthGuard roles={['admin', 'supervisor', 'curator']}>
        {id && <PageSimilarPages pageId={id} />}
      </AuthGuard>
    </div>
  );
}

export default function DocumentPageView() {
  const { id } = useParams<{ id: string }>();
  return <DocumentPageContent id={id!} />;
}
